import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '../useFetch'

import { CONFIG } from '../../config'
import { useAuth } from '../../contexts/AuthContext'
import { ApiError } from '../../types/exception/errorStatus'
import { WidgetDataResult } from '../../types/widget/widget'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'

const WIDGETS_PATH = `${CONFIG.BOUNCE_API}/api/v1/widgets`

export const useWidgetData = <T>(endpoint: string): WidgetDataResult<T> => {
  const { t } = useTranslation('exceptions')
  const { authInfo } = useAuth()
  const { fetcher } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)
  const fetcherRef = useRef(fetcher)
  const translateRef = useRef(t)
  const requestedEndpointRef = useRef(endpoint)
  const requestIdRef = useRef(0)
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const resetState = useCallback(() => {
    setError(null)
  }, [])

  useEffect(() => {
    fetcherRef.current = fetcher
  }, [fetcher])

  useEffect(() => {
    translateRef.current = t
  }, [t])

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current

    if (requestedEndpointRef.current !== endpoint) {
      requestedEndpointRef.current = endpoint
      setData(null)
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetcherRef.current<T>(
        `${WIDGETS_PATH}/${endpoint}`
      )

      if (requestId !== requestIdRef.current) {
        return
      }

      setData(response)
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return
      }

      console.error(error)
      setError(
        error instanceof ApiError
          ? getErrorMessage(error.data)
          : translateRef.current('exceptions:unknown')
      )
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [endpoint])

  useEffect(() => {
    void refetch()
  }, [refetch])

  return { data, loading, error, refetch, resetState }
}
